import { useEffect, useState } from "react";
import {
  Alert, Box, Button, FormControlLabel, Paper, Stack, Switch, ToggleButton,
  ToggleButtonGroup, Typography,
} from "@mui/material";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantsApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { DetailSkeleton } from "@/components/Skeletons";
import { ThemeSpecimen } from "@/components/ThemeSpecimen";
import { THEMES, THEME_ORDER, isThemeId, type ColorMode, type ThemeId } from "@/theme";
import { useThemePref } from "@/store/theme";
import { useResolvedColorMode } from "@/store/colorMode";

/**
 * Tenant appearance — the house palette, and whether users may depart from it.
 *
 * Path: /admin/appearance (registered in App.tsx). Writes are gated on
 * `system.feature_flag.manage`, the same permission as the other tenant-wide presentation toggles.
 *
 * The preview below each option is the real `ThemeSpecimen`, rendering real components through a
 * nested ThemeProvider. An admin picking a house style for a hospital needs to see the palettes
 * they are *not* currently running, and a hand-drawn swatch would go stale the first time a token
 * moved.
 */
export function AppearancePage() {
  const qc = useQueryClient();
  const setTenantPolicy = useThemePref((s) => s.setTenantPolicy);
  const activeMode = useResolvedColorMode();

  const q = useQuery({
    queryKey: ["tenant", "current"],
    queryFn: tenantsApi.current,
    staleTime: 5 * 60_000,
  });

  const [themeId, setThemeId] = useState<ThemeId | null>(null);
  const [allowOverride, setAllowOverride] = useState(true);
  // Which mode the previews render in. Defaults to whatever the admin is looking at, but both
  // ship, so both need checking before a palette is imposed on a whole site.
  const [previewMode, setPreviewMode] = useState<ColorMode>(activeMode);

  useEffect(() => {
    if (!q.data) return;
    setThemeId(isThemeId(q.data.defaultThemeId) ? q.data.defaultThemeId : "slate");
    setAllowOverride(q.data.allowUserThemeOverride);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => tenantsApi.setTheme(themeId ?? "slate", allowOverride),
    meta: { successMessage: "Appearance saved" },
    onSuccess: () => {
      // Apply immediately for whoever just saved, rather than making them reload to see it.
      setTenantPolicy(themeId ?? "slate", allowOverride);
      qc.invalidateQueries({ queryKey: ["tenant", "current"] });
    },
  });

  if (q.isLoading) return <DetailSkeleton />;
  if (q.error || !q.data) {
    return <Alert severity="error" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>;
  }

  const dirty =
    themeId !== null &&
    (themeId !== q.data.defaultThemeId || allowOverride !== q.data.allowUserThemeOverride);

  return (
    <Box sx={{ p: 3, maxWidth: 1080 }}>
      <PageHeader breadcrumbs={["Administration"]} title="Appearance" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: "68ch" }}>
        The palette users see before choosing one, and on every new device. Light or dark and row
        density stay with each user &mdash; those are legibility settings, and a night-shift terminal
        has a different need from a daylit reception desk.
      </Typography>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          sx={{ justifyContent: "space-between", alignItems: { sm: "center" }, gap: 1, mb: 2 }}
        >
          <Typography variant="overline" color="text.secondary">Default palette</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={previewMode}
            onChange={(_e, v: ColorMode | null) => v && setPreviewMode(v)}
            aria-label="Preview mode"
          >
            <ToggleButton value="light"><LightModeIcon sx={{ fontSize: 16, mr: 0.75 }} />Light</ToggleButton>
            <ToggleButton value="dark"><DarkModeIcon sx={{ fontSize: 16, mr: 0.75 }} />Dark</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
          }}
        >
          {THEME_ORDER.map((id) => {
            const selected = id === themeId;
            return (
              <Box
                key={id}
                component="button"
                type="button"
                onClick={() => setThemeId(id)}
                aria-pressed={selected}
                aria-label={THEMES[id].label}
                sx={{
                  p: 0.75,
                  textAlign: "left",
                  background: "none",
                  cursor: "pointer",
                  borderRadius: 2.5,
                  border: 2,
                  borderColor: selected ? "primary.main" : "transparent",
                  "&:hover": { borderColor: selected ? "primary.main" : "divider" },
                }}
              >
                <ThemeSpecimen themeId={id} mode={previewMode} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.75, px: 0.5, lineHeight: 1.4 }}
                >
                  {THEMES[id].blurb}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="overline" color="text.secondary">User override</Typography>
        <FormControlLabel
          sx={{ display: "block", mt: 1 }}
          control={
            <Switch
              checked={allowOverride}
              onChange={(e) => setAllowOverride(e.target.checked)}
            />
          }
          label="Let users pick their own palette"
        />
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "68ch" }}>
          {allowOverride
            ? "Users can change the palette from the Appearance menu in the top bar. The default above applies until they do."
            : "The default above is the only palette. The picker still shows, but read-only — a user's existing choice is kept and returns if you switch this back on."}
        </Typography>
      </Paper>

      {save.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(save.error)}</Alert>
      ) : null}

      <Button
        variant="contained"
        disabled={!dirty || save.isPending}
        onClick={() => save.mutate()}
      >
        Save appearance
      </Button>
    </Box>
  );
}
